import React, { useEffect, useState } from 'react';
import { api, PhotoChecklistTemplateDto, ShotSpecDto } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';

// ── Photo Checklists (core primitive) ──────────────────────────────────────────
// Template authoring — a named, ordered list of required/optional shots a job can be assigned
// (assignment itself happens wherever a job is booked/dispatched for a vertical that needs it,
// not here). See PhotoChecklistTemplate's doc comment in Models.cs for the completion-gate
// rationale.

export function PhotoChecklistPanel() {
    const [templates, setTemplates] = useState<PhotoChecklistTemplateDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<PhotoChecklistTemplateDto | 'new' | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getPhotoChecklistTemplates()
            .then(setTemplates)
            .catch(e => setError(e.message || 'Failed to load templates.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (editing) {
        return (
            <TemplateForm
                template={editing === 'new' ? null : editing}
                onCancel={() => setEditing(null)}
                onSaved={t => {
                    setTemplates(prev => editing === 'new' ? [...prev, t] : prev.map(x => x.id === t.id ? t : x));
                    setEditing(null);
                }}
            />
        );
    }

    return (
        <div style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Photo Checklists</h2>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Required shot lists a job can be assigned — jobs missing a required shot can't be marked complete</div>
                </div>
                <Btn onClick={() => setEditing('new')}>+ New checklist</Btn>
            </div>

            <Alert message={error} />

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {templates.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No checklists yet.</div>}
                    {templates.map((t, i) => {
                        const requiredCount = t.shotSpecs.filter(s => s.required).length;
                        return (
                            <div key={t.id} onClick={() => setEditing(t)} style={{
                                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer',
                                borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                            }}>
                                <span style={{
                                    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                    background: t.isActive ? C.greenBg : '#F5F5F5',
                                    color: t.isActive ? C.greenMid : C.textMuted,
                                    flexShrink: 0, minWidth: 60, textAlign: 'center',
                                }}>{t.isActive ? 'Active' : 'Inactive'}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{t.name}</div>
                                    <div style={{ fontSize: 12, color: C.textSub }}>
                                        {t.shotSpecs.length} shot{t.shotSpecs.length !== 1 ? 's' : ''} · {requiredCount} required
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Card>
            )}
        </div>
    );
}

function TemplateForm({ template, onCancel, onSaved }: {
    template: PhotoChecklistTemplateDto | null;
    onCancel: () => void;
    onSaved: (t: PhotoChecklistTemplateDto) => void;
}) {
    const [name, setName] = useState(template?.name ?? '');
    const [isActive, setIsActive] = useState(template?.isActive ?? true);
    const [shots, setShots] = useState<ShotSpecDto[]>(template?.shotSpecs ?? []);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const addShot = () => setShots(prev => [...prev, { label: '', required: true, description: null }]);
    const updateShot = (i: number, patch: Partial<ShotSpecDto>) =>
        setShots(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const removeShot = (i: number) => setShots(prev => prev.filter((_, idx) => idx !== i));

    const save = async () => {
        if (!name.trim()) { setError('Name is required.'); return; }
        if (shots.some(s => !s.label.trim())) { setError('Every shot needs a label.'); return; }
        setSaving(true); setError('');
        try {
            const req = { name: name.trim(), shotSpecs: shots.map(s => ({ ...s, label: s.label.trim(), description: s.description?.trim() || null })), isActive };
            const saved = template
                ? await api.updatePhotoChecklistTemplate(template.id, req)
                : await api.createPhotoChecklistTemplate(req);
            onSaved(saved);
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 640, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>{template ? 'Edit checklist' : 'New checklist'}</h2>
                {template && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textSub, cursor: 'pointer' }}>
                        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} /> Active
                    </label>
                )}
            </div>
            <Alert message={error} />

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Property Condition Inspection"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Required shots</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {shots.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input value={s.label} onChange={e => updateShot(i, { label: e.target.value })} placeholder="Shot label, e.g. Front exterior"
                                style={{ flex: 2, padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text }} />
                            <input value={s.description ?? ''} onChange={e => updateShot(i, { description: e.target.value })} placeholder="Notes (optional)"
                                style={{ flex: 2, padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text }} />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textSub, flexShrink: 0, cursor: 'pointer' }}>
                                <input type="checkbox" checked={s.required} onChange={e => updateShot(i, { required: e.target.checked })} /> Required
                            </label>
                            <button onClick={() => removeShot(i)} style={{ background: 'none', border: 'none', color: '#D84040', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>×</button>
                        </div>
                    ))}
                    {shots.length === 0 && <div style={{ fontSize: 13, color: C.textMuted }}>No shots added yet.</div>}
                </div>
                <Btn variant="outline" onClick={addShot} style={{ marginTop: 10 }}>+ Add shot</Btn>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save checklist'}</Btn>
            </div>
        </Card>
    );
}
